import { useCallback, useEffect, useState } from "react";
import { api } from "../lib/api";
import { BUILD_COMMIT, BUILD_COMMITTED_AT } from "../buildInfo.generated";
import { diagSnapshot, getDiag, getReportableTurnDiag } from "../lib/leviSpeech";

const KEY = "jc:support:diag";
const AT_KEY = "jc:support:diag:enabled_at";
export const SUPPORT_DIAG_TTL_MS = 60 * 60 * 1000;
export const VOICE_DIAG_EVENT_LIMIT = 100;
export const VOICE_DIAG_WINDOW_MS = 10 * 60 * 1000;
const SECRET_KEY = /(authorization|cookie|token|secret|password|api[_-]?key|jwt)/i;
const SDR_REFERENCE = /^SDR-\d{6}-[A-F0-9]{4}$/;

function _readEnabled() {
  try {
    if (typeof window === "undefined") return false;
    const v = window.sessionStorage.getItem(KEY);
    if (v !== "1") return false;
    const at = Number(window.sessionStorage.getItem(AT_KEY) || 0);
    if (!at || Date.now() - at > SUPPORT_DIAG_TTL_MS) {
      window.sessionStorage.removeItem(KEY);
      window.sessionStorage.removeItem(AT_KEY);
      return false;
    }
    return true;
  } catch { return false; }
}

export function isSupportDiagnosticsEnabled() { return _readEnabled(); }

export function enableSupportDiagnostics() {
  try {
    window.sessionStorage.setItem(KEY, "1");
    window.sessionStorage.setItem(AT_KEY, String(Date.now()));
    window.dispatchEvent(new CustomEvent("jc:support:diag:changed"));
  } catch { /* noop */ }
}

export function disableSupportDiagnostics() {
  try {
    window.sessionStorage.removeItem(KEY);
    window.sessionStorage.removeItem(AT_KEY);
    window.dispatchEvent(new CustomEvent("jc:support:diag:changed"));
  } catch { /* noop */ }
}

export function useSupportDiagnostics() {
  const [enabled, setEnabled] = useState(() => _readEnabled());
  const refresh = useCallback(() => setEnabled(_readEnabled()), []);
  useEffect(() => {
    const on = () => refresh();
    window.addEventListener("jc:support:diag:changed", on);
    const t = window.setInterval(refresh, 60 * 1000);
    return () => {
      window.removeEventListener("jc:support:diag:changed", on);
      window.clearInterval(t);
    };
  }, [refresh]);
  return { enabled, enable: enableSupportDiagnostics,
           disable: disableSupportDiagnostics, refresh };
}

function _redactString(value) {
  return String(value)
    .replace(/Bearer\s+[A-Za-z0-9._~+\/-]+=*/gi, "[REDACTED]")
    .replace(/\beyJ[A-Za-z0-9_-]{10,}\.[A-Za-z0-9_-]{10,}\.[A-Za-z0-9_-]{10,}\b/g,
      "[REDACTED]")
    .replace(/\b(?:sk|re)_[A-Za-z0-9_-]{12,}\b/g, "[REDACTED]");
}

export function redactDiagnosticValue(value, key = "") {
  if (SECRET_KEY.test(String(key))) return "[REDACTED]";
  if (Array.isArray(value)) {
    return value.map((item) => redactDiagnosticValue(item));
  }
  if (value && typeof value === "object") {
    const out = {};
    Object.entries(value).forEach(([childKey, childValue]) => {
      out[childKey] = redactDiagnosticValue(childValue, childKey);
    });
    return out;
  }
  return typeof value === "string" ? _redactString(value) : value;
}

function _environmentName(hostname) {
  if (/\.preview\.emergentagent\.com$/i.test(hostname || "")) return "preview";
  if (/^(localhost|127\.0\.0\.1)$/i.test(hostname || "")) return "local";
  return "production";
}

function _deviceSnapshot(nav, w) {
  const ua = nav.userAgent || "";
  const connection = nav.connection || nav.mozConnection || nav.webkitConnection;
  return {
    user_agent: ua.slice(0, 320),
    platform: nav.platform || null,
    os: /iPhone|iPad|iPod/i.test(ua) ? "iOS/iPadOS"
      : (/Android/i.test(ua) ? "Android" : null),
    browser: /CriOS/i.test(ua) ? "Chrome iOS"
      : (/FxiOS/i.test(ua) ? "Firefox iOS"
        : (/Safari/i.test(ua) && !/Chrome|Chromium/i.test(ua) ? "Safari" : null)),
    is_mobile: /Mobi|Android|iPhone|iPad|iPod/i.test(ua),
    viewport: { width: w.innerWidth || null, height: w.innerHeight || null },
    dpr: w.devicePixelRatio || null,
    locale: nav.language || null,
    online: typeof nav.onLine === "boolean" ? nav.onLine : null,
    connection: connection ? {
      effective_type: connection.effectiveType || null,
      downlink: connection.downlink || null,
      rtt: connection.rtt || null,
      save_data: connection.saveData || false,
    } : null,
  };
}

export function isAuthoritativeSupportReference(value) {
  return SDR_REFERENCE.test(String(value || ""));
}

const _hasEvent = (events, name) => events.some((entry) => entry.evt === name);
const _lastEvent = (events, name) => [...events].reverse()
  .find((entry) => entry.evt === name) || null;

export function summarizeReportedVoiceTurn(turn = {}) {
  const events = Array.isArray(turn.events) ? turn.events : [];
  const endpoint = _lastEvent(events, "vad:endpoint")
    || _lastEvent(events, "vad:silence_endpoint");
  const cadence = endpoint?.analyzer_cadence || null;
  const chunks = events.filter((entry) => entry.evt === "recorder:chunk");
  const chunksDuringBelowThreshold = chunks.filter((entry) => {
    const c = entry.vad_correlation;
    return c && Number.isFinite(c.current_rms) && Number.isFinite(c.threshold)
      && c.current_rms < c.threshold;
  });
  const trackTransitions = events.filter((entry) => [
    "mic_track:mute", "mic_track:unmute", "mic_track:ended",
    "media_devices:devicechange",
  ].includes(entry.evt));
  const audioContextChanges = events.filter((entry) =>
    entry.evt === "vad:audioctx_statechange");
  const coreLifecycle = {
    microphone_acquisition_requested: _hasEvent(events, "mic:acquire_requested"),
    microphone_acquisition_completed: _hasEvent(events, "mic:acquired"),
    recorder_started: _hasEvent(events, "recorder:started"),
    vad_armed: _hasEvent(events, "vad:armed"),
    recorder_stop_requested: _hasEvent(events, "recorder:stop_requested"),
    recorder_completed: _hasEvent(events, "voice_capture:completed"),
    submission_requested: _hasEvent(events, "voice_submit:requested")
      || (events.some((entry) => entry.evt === "command_submission:started"
        && entry.has_audio_file === true)),
    vad_disposed: _hasEvent(events, "vad:disposed"),
  };
  const completeTargetTurnRetained = turn.retained_from_acquisition === true
    && Object.values(coreLifecycle).every(Boolean);
  let supportedCause = "insufficient_analyzer_observations";
  if (cadence?.endpoint_followed_multi_second_scheduling_gap) {
    supportedCause = "analyzer_scheduling_gap_observed";
  } else if (cadence?.signal_pattern === "near_zero") {
    supportedCause = "analyzer_signal_fell_near_zero";
  } else if (cadence?.signal_pattern === "narrowly_below_threshold") {
    supportedCause = "analyzer_rms_stayed_narrowly_below_threshold";
  } else if (cadence?.signal_pattern === "repeated_below_threshold"
             && cadence?.sufficient_below_threshold_observation_coverage) {
    supportedCause = "repeated_below_threshold_analyzer_readings";
  }
  const unresolved = [
    "threshold_sensitivity_vs_iphone_signal_attenuation",
    "media_recorder_audibility_without_raw_audio",
    "web_audio_vs_media_recorder_signal_path_difference",
    "microphone_route_behavior_not_exposed_by_browser",
  ];
  if (!cadence?.analyzer_sampled_continuously) {
    unresolved.push("browser_analyzer_scheduling_continuity");
  }
  return {
    reported_failure_turn: turn.turn_id || null,
    listening_epoch: turn.listening_epoch || null,
    selection_reason: turn.selection_reason || null,
    complete_target_turn_retained: completeTargetTurnRetained,
    lifecycle_evidence: coreLifecycle,
    event_count: events.length,
    analyzer_sampled_continuously: cadence?.analyzer_sampled_continuously ?? null,
    analyzer_cadence: cadence,
    rms_signal_pattern: cadence?.signal_pattern || "unavailable",
    microphone_track_transition_count: trackTransitions.length,
    microphone_track_transitions: trackTransitions,
    audio_context_state_change_count: audioContextChanges.length,
    audio_context_state_changes: audioContextChanges,
    recorder_chunk_count: chunks.length,
    recorder_chunks_continued_during_below_threshold_interval:
      chunksDuringBelowThreshold.length > 0,
    recorder_chunks_during_below_threshold_interval_count:
      chunksDuringBelowThreshold.length,
    tts_event_count: events.filter((entry) =>
      String(entry.evt || "").startsWith("serverTts:")).length,
    barge_in_event_count: events.filter((entry) =>
      String(entry.evt || "").startsWith("bargeIn:")).length,
    cause_assessment: {
      supported: supportedCause,
      unresolved,
      raw_audio_available: false,
    },
  };
}

export function collectClientSnapshot({
  includePrivateContent = false,
  backendBuild = null,
} = {}) {
  const w = typeof window !== "undefined" ? window : {};
  const nav = typeof navigator !== "undefined" ? navigator : {};
  const qa = w.__leviQAState || {};
  const parity = w.__leviParityDiag || {};
  const hostname = w.location?.hostname || "";
  const reportedTurn = getReportableTurnDiag(VOICE_DIAG_WINDOW_MS);
  const reportedTurnAnalysis = summarizeReportedVoiceTurn(reportedTurn);
  const snapshot = {
    captured_at: new Date().toISOString(),
    environment: _environmentName(hostname),
    build: {
      backend_authoritative: backendBuild,
      frontend_compiled_commit: BUILD_COMMIT || null,
      frontend_committed_at: BUILD_COMMITTED_AT || null,
    },
    app: {
      route: w.location?.pathname || null,
      origin: w.location?.origin || null,
      visibility_state: typeof document !== "undefined"
        ? document.visibilityState : null,
      has_focus: typeof document !== "undefined" && document.hasFocus
        ? document.hasFocus() : null,
    },
    device: _deviceSnapshot(nav, w),
    session: {
      levi_session_id: qa.levi_session_id || qa.session_id || null,
      client_speech_turn_id: reportedTurn.turn_id || null,
      listening_epoch: reportedTurn.listening_epoch || null,
    },
    voice: {
      mode: qa.voice_mode || null,
      voice_id: qa.voice_id || null,
      stt_source: qa.stt_source || null,
      tts_source_last: w.__leviLastTtsSource || qa.tts_source || null,
      state: diagSnapshot(),
      interim_transcription_available: false,
    },
    event_ring: {
      scope: "reported_voice_turn",
      count_limited: false,
      window_ms: VOICE_DIAG_WINDOW_MS,
      target_turn_id: reportedTurn.turn_id || null,
      target_listening_epoch: reportedTurn.listening_epoch || null,
      retained_from_acquisition: reportedTurn.retained_from_acquisition === true,
      selection_reason: reportedTurn.selection_reason || null,
      first_event_at: reportedTurn.first_event_at || null,
      last_event_at: reportedTurn.last_event_at || null,
      global_recent_event_count: getDiag(
        VOICE_DIAG_EVENT_LIMIT, VOICE_DIAG_WINDOW_MS).length,
      events: reportedTurn.events || [],
    },
    reported_turn_analysis: reportedTurnAnalysis,
    network: { recent_calls: (qa.recent_calls || []).slice(-20) },
    parity_diag: parity,
    privacy: {
      transcript_or_private_content_included: !!includePrivateContent,
      raw_audio_included: false,
      raw_audio_collected: false,
    },
  };
  if (includePrivateContent) {
    snapshot.private_content = {
      last_transcript: qa.last_transcript || null,
      last_reply: qa.last_reply || null,
    };
  }
  return redactDiagnosticValue(snapshot);
}

export async function collectAuthoritativeClientSnapshot(options = {}) {
  let backendBuild = null;
  try {
    const response = await api.get("/build/head");
    backendBuild = {
      commit: response?.data?.commit || null,
      committed_at: response?.data?.committed_at || null,
    };
  } catch {
    backendBuild = { unavailable: true };
  }
  return collectClientSnapshot({ ...options, backendBuild });
}